/**
 * Drive statistics (audit KPIs + charts) side page.
 */

import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { StatsChartsPanel } from './StatsChartsPanel.jsx'
import { SidePageLoader } from './SidePageLoader.jsx'
import {
  applyQuery,
  createDriveI18n,
  fetchJson,
  formatBytes,
  formatDate,
  jsonUrl
} from './side-utils.js'
import { statistics_drive_index_path } from '../../routes'
import { DriveSideChrome } from './DriveSideChrome.jsx'

export function StatisticsPage() {
  const i18n = createDriveI18n()
  const t = (key, opts) => i18n.t(key, opts)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async (search = window.location.search) => {
    setError(null)
    setLoading(true)
    try {
      setData(await fetchJson(jsonUrl(search)))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function changeFilters({ period, source, personId }) {
    const url = applyQuery({
      period: period ?? data?.period_days,
      source: source === undefined ? data?.source_filter : source,
      person_id: personId === undefined ? data?.person_filter_id : personId
    })
    history.pushState({}, '', url)
    load(url.search)
  }

  if (error && !data) {
    return (
      <DriveSideChrome titleKey="drive.statistics.nav" titleIcon="bi-bar-chart-line">
        <div class="alert alert-danger">{error}</div>
      </DriveSideChrome>
    )
  }
  if (!data || loading) {
    return (
      <DriveSideChrome titleKey="drive.statistics.nav" titleIcon="bi-bar-chart-line">
        <SidePageLoader label={t('drive.loading')} />
      </DriveSideChrome>
    )
  }

  const stats = data.stats || {}
  const events = data.events || []
  const people = data.people || []
  const sources = data.sources || []
  const periods = data.periods || ['7', '30', '90']

  return (
    <DriveSideChrome titleKey="drive.statistics.nav" titleIcon="bi-bar-chart-line">
    <div class="drive-container drive-statistics">
      {error && <div class="alert alert-warning">{error}</div>}

      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-end g-2">
            <div class="col-md-3">
              <label class="form-label">{t('drive.statistics.period_filter')}</label>
              <select
                class="form-select"
                value={data.period_days}
                onChange={(e) => changeFilters({ period: e.target.value })}
              >
                {periods.map((d) => (
                  <option key={d} value={d}>
                    {t(`drive.statistics.period_${d}`)}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label">{t('drive.statistics.source_filter')}</label>
              <select
                class="form-select"
                value={data.source_filter || ''}
                onChange={(e) => changeFilters({ source: e.target.value || null })}
              >
                <option value="">{t('drive.statistics.all_sources')}</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {t(`drive.statistics.sources.${s}`)}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">{t('drive.statistics.person_filter')}</label>
              <select
                class="form-select"
                value={data.person_filter_id || ''}
                onChange={(e) =>
                  changeFilters({ personId: e.target.value || null })
                }
              >
                <option value="">{t('drive.statistics.all_people')}</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-md-2">
              <a href={statistics_drive_index_path()} class="btn btn-outline-secondary">
                {t('drive.statistics.clear_filters')}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-body">
              <div class="text-muted small">
                {t('drive.statistics.uploads_period', { days: data.period_days })}
              </div>
              <div class="fs-3">{stats.upload_count}</div>
              <small class="text-muted">{formatBytes(stats.upload_bytes)}</small>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card">
            <div class="card-body">
              <div class="text-muted small">
                {t('drive.statistics.downloads_period', { days: data.period_days })}
              </div>
              <div class="fs-3">{stats.download_count}</div>
              <small class="text-muted">{formatBytes(stats.download_bytes)}</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">{t('drive.statistics.by_action')}</div>
            <div class="card-body">
              {stats.by_action && Object.keys(stats.by_action).length > 0 ? (
                <ul class="mb-0">
                  {Object.entries(stats.by_action).map(([action, count]) => (
                    <li key={action}>
                      <code>
                        {t(`drive.statistics.actions.${action}`, { defaultValue: action })}
                      </code>
                      : {count}
                    </li>
                  ))}
                </ul>
              ) : (
                <span class="text-muted">{t('drive.statistics.no_events')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <StatsChartsPanel stats={stats} chartI18n={data.chart_i18n} />

      <div class="card">
        <div class="card-header">{t('drive.statistics.audit_log')}</div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>{t('drive.statistics.when')}</th>
                  <th>{t('drive.statistics.source')}</th>
                  <th>{t('drive.statistics.person')}</th>
                  <th>{t('drive.statistics.action')}</th>
                  <th>{t('drive.statistics.direction')}</th>
                  <th>{t('drive.statistics.path')}</th>
                  <th>{t('drive.statistics.size')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, idx) => (
                  <tr key={idx}>
                    <td>{formatDate(event.created_at)}</td>
                    <td>{t(`drive.statistics.sources.${event.source}`)}</td>
                    <td>{event.person_name || '—'}</td>
                    <td>
                      <code>
                        {t(`drive.statistics.actions.${event.action}`, {
                          defaultValue: event.action
                        })}
                      </code>
                    </td>
                    <td>
                      {event.direction
                        ? t(`drive.statistics.directions.${event.direction}`)
                        : '—'}
                    </td>
                    <td class="text-truncate" style="max-width:320px">
                      {event.path}
                    </td>
                    <td>{event.byte_size != null ? formatBytes(event.byte_size) : '—'}</td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colspan="7" class="text-muted text-center">
                      {t('drive.statistics.no_events')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </DriveSideChrome>
  )
}
